import React from "react";

interface CheckboxProps {
  id?: string;
  name?: string;
  label?: React.ReactNode;
  externalLabel?: React.ReactNode;
  value?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  // Support both patterns for backward compatibility
  onChange?: ((e: React.ChangeEvent<HTMLInputElement>) => void) | ((checked: boolean) => void);
  className?: string;
  labelClassName?: string;
  disabled?: boolean;
  error?: boolean;
  success?: boolean;
  hint?: string;
  required?: boolean;
  /** Id of the element that explains this option (for example why it is disabled). */
  describedBy?: string;
}

const Checkbox: React.FC<CheckboxProps> = ({
  id,
  name,
  label,
  externalLabel,
  value,
  checked,
  defaultChecked,
  onChange,
  className = "",
  labelClassName = "",
  disabled = false,
  error = false,
  success = false,
  hint,
  required = false,
  describedBy,
}) => {
  // Handle both onChange patterns for backward compatibility
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!onChange) return;
    
    const isChecked = e.target.checked;
    
    // Improved detection: Default to boolean pattern (modern React standard)
    // Check function signature to determine pattern
    const funcString = onChange.toString();
    
    // Detect event pattern: function explicitly uses event properties
    const usesEventPattern = funcString.includes('.target') || 
                           funcString.includes('.currentTarget') ||
                           funcString.includes('preventDefault') ||
                           funcString.includes('stopPropagation');
    
    if (usesEventPattern) {
      // Event pattern: (e: React.ChangeEvent<HTMLInputElement>) => void
      (onChange as (e: React.ChangeEvent<HTMLInputElement>) => void)(e);
    } else {
      // Boolean pattern: (checked: boolean) => void (default for modern React)
      // This works for useState setters and most modern handlers
      (onChange as (checked: boolean) => void)(isChecked);
    }
  };

  // Enhanced styling from TailAdmin with custom appearance
  // Use items-center when no label prop (for external labels), items-start when label is provided
  let containerClasses = `flex ${label ? 'items-start' : 'items-center'} group ${className}`;
  let checkboxClasses = `w-5 h-5 appearance-none cursor-pointer border rounded-md checked:bg-brand-500 checked:border-transparent focus:ring-2 focus:ring-brand-500/20`;
  
  if (disabled) {
    checkboxClasses += " border-gray-300 bg-gray-100 cursor-not-allowed dark:border-gray-700 dark:bg-gray-800 opacity-60";
    containerClasses += " cursor-not-allowed opacity-60";
  } else if (error) {
    checkboxClasses += " border-danger focus:border-danger focus:ring-danger/20";
  } else if (success) {
    checkboxClasses += " border-success focus:border-success focus:ring-success/20";
  } else {
    checkboxClasses += " border-gray-300 dark:border-gray-700";
  }
  
  let labelClasses = `ml-3 text-sm font-medium cursor-pointer leading-relaxed ${labelClassName}`;
  
  if (disabled) {
    labelClasses += " text-gray-400 cursor-not-allowed";
  } else if (error) {
    labelClasses += " text-danger";
  } else if (success) {
    labelClasses += " text-success";
  } else {
    labelClasses += " text-gray-800 dark:text-gray-200";
  }

  // Normalize checked to always be a boolean (prevent object/truthy coercion issues)
  const normalizedChecked = typeof checked === 'boolean' ? checked : Boolean(checked);
  
  // When no label prop is provided, don't wrap in label element (for external labels)
  const checkboxElement = (
    <div className="relative w-5 h-5 flex items-center justify-center">
      <input
        type="checkbox"
        id={id}
        name={name}
        value={value}
        checked={normalizedChecked}
        defaultChecked={defaultChecked}
        onChange={handleChange}
        disabled={disabled}
        required={required}
        aria-describedby={describedBy}
        className={checkboxClasses}
      />
      {/* Custom SVG checkmark - shows when checked */}
      {checked && (
        <svg
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
        >
          <path
            d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
            stroke="white"
            strokeWidth="1.94437"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {/* Disabled state checkmark */}
      {disabled && normalizedChecked && (
        <svg
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
        >
          <path
            d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
            stroke="#E4E7EC"
            strokeWidth="2.33333"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );

  // External label pattern - wraps checkbox and label in proper flex container
  if (externalLabel) {
    return (
      <div className="relative">
        <label htmlFor={id} className={`flex items-center gap-2 cursor-pointer ${disabled ? 'cursor-not-allowed opacity-60' : ''} ${className}`}>
          <div className="flex-shrink-0">
            {checkboxElement}
          </div>
          <span className={`text-sm font-medium ${labelClassName} ${
            disabled
              ? "text-gray-400 cursor-not-allowed"
              : error
                ? "text-danger"
                : success
                  ? "text-success"
                  : "text-gray-700 dark:text-gray-300"
          }`}>
            {externalLabel}
            {required && <span className="ml-1 text-danger">*</span>}
          </span>
        </label>
        
        {hint && (
          <p
            className={`mt-1.5 text-xs ${
              error
                ? "text-danger"
                : success
                  ? "text-success"
                  : "text-gray-500"
            }`}
          >
            {hint}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      {label ? (
        <label className={containerClasses}>
          {checkboxElement}
          <span className={`${labelClasses} flex-1`}>
            {label}
            {required && <span className="ml-1 text-danger">*</span>}
          </span>
        </label>
      ) : (
        checkboxElement
      )}
      
      {hint && (
        <p
          className={`mt-1.5 text-xs ${
            error
              ? "text-danger"
              : success
                ? "text-success"
                : "text-gray-500"
          }`}
        >
          {hint}
        </p>
      )}
    </div>
  );
};

export default Checkbox; 